(ns client-portal.client.user-management.components
  (:require [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.user-management.data :as um.d]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(style/add-css "@keyframes um-save-bar-in { from { opacity: 0; transform: translateY(8px); } }"
               "@media (prefers-reduced-motion: no-preference) { .um-save-bar { animation: um-save-bar-in 160ms ease-out; } }"
               ;; The section rail sits beside the content on wide screens and becomes a scrolling row above it on narrow
               ".um-layout { display: flex; flex-direction: column; min-height: 100%; }"
               ".um-section-panel { border-style: solid; border-width: 0 0 1px 0; flex-shrink: 0; }"
               ".um-section-list { display: flex; gap: 4px; overflow-x: auto; padding: 12px 16px; }"
               ".um-section-list > button { flex-shrink: 0; white-space: nowrap; }"
               "@media (min-width: 768px) {"
               " .um-layout { flex-direction: row; }"
               " .um-section-panel { border-width: 0 1px 0 0; width: 232px; }"
               " .um-section-list { flex-direction: column; overflow-x: visible; padding: 16px 12px; position: sticky; top: 0; } }"
               ;; Label and description on the left, the control on the right; stacked on narrow screens
               ".um-field-row { display: grid; grid-template-columns: minmax(0, 1fr); row-gap: 12px; padding: 20px 0; }"
               "@media (min-width: 1024px) {"
               " .um-field-row { grid-template-columns: minmax(0, 4fr) minmax(0, 8fr); column-gap: 32px; } }"
               ".um-application-row { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 8px 12px; }"
               "@media (min-width: 640px) {"
               " .um-application-row { grid-template-columns: auto minmax(120px, 1fr) minmax(0, 2fr); } }"
               ".um-application-row > .um-roles { grid-column: 1 / -1; }"
               "@media (min-width: 640px) { .um-application-row > .um-roles { grid-column: auto; } }")

(def max-content-width 1040)

(def option-class-name
  (str "flex w-full items-center gap-2 rounded-md text-left outline-none transition-colors focus-visible:ring-[3px] "
       "focus-visible:ring-ring/50 hover:bg-up-background-hover disabled:pointer-events-none disabled:opacity-50"))

(def popover-trigger-class-name
  (str "inline-flex items-center gap-2 overflow-hidden rounded-md px-2 py-2 text-sm font-medium text-foreground "
       "transition-colors hover:bg-accent hover:text-accent-foreground outline-none focus-visible:ring-[3px] "
       "focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"))

(defn secondary-text [{:keys [style]} & contents]
  (into [:span {:style (merge {:color (style/alc-color "Text/secondary")
                               :font-size 13
                               :line-height 1.45}
                              style)}]
        contents))

(defn error-text [& contents]
  (into [:span {:role "alert"
                :style {:color (style/alc-color "Common/error")
                        :font-size 13
                        :line-height 1.45}}]
        contents))

(defn field-row [{:keys [label description]} & contents]
  [:div {:class-name "um-field-row"
         :style {:border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))}}
   [:div {:style {:display :flex
                  :flex-direction :column
                  :gap 4}}
    [:span {:style {:color (style/alc-color "Text/primary_header")
                    :font-size 15
                    :font-weight 500
                    :line-height 1.4}}
     label]
    (when description
      [secondary-text {:style {:max-width "46ch"}}
       description])]
   (into [:div {:style {:display :flex
                        :flex-direction :column
                        :gap 12
                        :min-width 0}}]
         contents)])

(defn initials [label]
  (->> (s/split (or label "") #"[\s()]+")
       (remove s/blank?)
       (take 2)
       (map first)
       (apply str)
       (s/upper-case)))

(defn organization-mark
  "An organization's logo, or its initials on its brand color"
  [{:keys [label org-logo primary-color]} & [{:keys [size]
                                              :or {size 28}}]]
  (let [base {:align-items :center
              :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
              :border-radius "50%"
              :display :inline-flex
              :flex (str "0 0 " size "px")
              :height size
              :justify-content :center
              :width size}]
    (if (not-empty org-logo)
      [:span {:aria-hidden true
              :style (merge base
                            {:background-color (style/alc-color "Common/White")
                             :background-image (str "url(" org-logo ")")
                             :background-position "50% 50%"
                             :background-repeat :no-repeat
                             :background-size "70%"})}]
      [:span {:aria-hidden true
              :style (merge base
                            {:background-color (or (not-empty primary-color) (style/alc-color "Background/tertiary"))
                             :color (if (not-empty primary-color)
                                      (style/alc-color "Common/White")
                                      (style/alc-color "Text/secondary"))
                             :font-size (* size 0.38)
                             :font-weight 500
                             :letter-spacing "0.02em"})}
       (initials label)])))

(defn organization-name [organization & [mark-args]]
  [:span {:style {:align-items :center
                  :display :inline-flex
                  :gap 8
                  :min-width 0}}
   [organization-mark organization mark-args]
   [:span {:style {:overflow :hidden
                   :text-overflow :ellipsis
                   :white-space :nowrap}}
    (:label organization)]])

(defn concept-mark [concept-id]
  [:span {:aria-hidden true
          :style {:background-color (style/alc-color "Common/White")
                  :background-image (str "url(" (concepts.d/concept-logo-url concept-id) ")")
                  :background-position "50% 50%"
                  :background-repeat :no-repeat
                  :background-size :contain
                  :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                  :border-radius "50%"
                  :flex "0 0 24px"
                  :height 24
                  :width 24}}])

(def status-colors
  {:active "Common/success_hex"
   :invited "Common/active"
   :expired "Chart/orange"
   :inactive "Common/error"})

(defn status-badge [status]
  (if status
    [:span {:style {:align-items :center
                    :display :inline-flex
                    :gap 6
                    :white-space :nowrap}}
     [:span {:aria-hidden true
             :style {:background-color (style/alc-color (status-colors status))
                     :border-radius "50%"
                     :flex "0 0 8px"
                     :height 8
                     :width 8}}]
     (um.d/status-labels status)]
    [secondary-text {} "—"]))

(defn internal-badge []
  [:span {:title "Only @soundhound.com addresses can have this role"
          :style {:border (str "1px solid " (style/alc-color "Border/divider"))
                  :border-radius 4
                  :color (style/alc-color "Text/secondary")
                  :font-size 11
                  :line-height 1.5
                  :padding "0 6px"
                  :white-space :nowrap}}
   "Internal Only"])

(defn role-pills [labels]
  (into [:span {:style {:display :flex
                        :flex-wrap :wrap
                        :gap 4}}]
        (for [label labels]
          [alc/up-badge {:variant "outline"
                         :class-name "rounded-full font-normal"
                         :style {:background-color (style/alc-color "Background/tertiary")
                                 :border-color (style/alc-color "Border/divider")
                                 :color (style/alc-color "Text/primary")}}
           label])))

(defn tenant-dropdown
  "Switches the tenant an admin manages, kept in the URL. Managers only ever see their own organization."
  []
  (when-not @(re/subscribe [:user-management/manager?-])
    (let [tenants (um.d/rows @(re/subscribe [:user-management/tenant-list-]))
          selected-id (or (some-> @(re/subscribe [:user-management/current-tenant-]) (util/parse-int))
                          (some #(when (:default %) (:id %)) tenants))]
      [alc/up-popover-button-dropdown {:placeholder "Tenant"
                                       :fixed-icon-type :LayersIcon
                                       :align :start
                                       :disabled (nil? tenants)
                                       :options (mapv (fn [{:keys [id label]}]
                                                        {:text (str label " Tenant")
                                                         :value id
                                                         :selected? (= id selected-id)})
                                                      tenants)
                                       :on-change #(re/dispatch [:user-management/set-tenant %])}])))

(defn toolbar [& contents]
  (into [:div {:style {:align-items :center
                       :display :flex
                       :flex-wrap :wrap
                       :gap 8
                       :max-width max-content-width}}]
        contents))

(defn spacer []
  [:span {:style {:flex 1}}])

(defn count-text [n singular plural]
  (str n " " (if (= n 1) singular plural)))

(defn join-words
  "a, b and c"
  [words]
  (let [words (vec words)]
    (case (count words)
      0 ""
      1 (first words)
      (str (s/join ", " (butlast words)) " and " (last words)))))

(defn confirm-dialog
  "Asks before something that can't be undone"
  [{:keys [title message confirm-text busy? on-confirm]}]
  [alc/dialog {:title title
               :footer [:<>
                        [alc/up-button {:variant "destructive"
                                        :loading? busy?
                                        :on-click on-confirm}
                         confirm-text]
                        [alc/up-button {:on-click #(re/dispatch [:dialog/hide])}
                         "Keep It"]]}
   [:p {:style {:color (style/alc-color "Text/primary")
                :font-size 14
                :line-height 1.5
                :margin 0
                :max-width "52ch"}}
    message]])
